import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import rtoAPI from "../../api/rtoAPI";
import "./RtoWarehouseScanModal.css";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";

const normalizeScanKey = (value) => String(value || "").trim().toLowerCase();
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const extractScanToken = (rawValue) => {
  const text = String(rawValue || "").trim();
  if (!text) return "";

  try {
    const parsedUrl = new URL(text);
    const queryToken =
      parsedUrl.searchParams.get("awb") ||
      parsedUrl.searchParams.get("awb_number") ||
      parsedUrl.searchParams.get("orderId") ||
      parsedUrl.searchParams.get("order_id") ||
      parsedUrl.searchParams.get("id");
    if (queryToken) return String(queryToken).trim();
  } catch {
    // Ignore invalid URL scanner payloads.
  }

  const labeled = text.match(/(?:awb|order[\s_-]*id)[:=\s#/]*([A-Za-z0-9_-]+)/i);
  if (labeled?.[1]) return labeled[1].trim();

  return text;
};

const formatDispatchDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatItemsSummary = (items, fallback = "-") => {
  if (!Array.isArray(items) || items.length === 0) return String(fallback || "-");
  return items
    .map((item) => {
      const name = String(item?.product_name || item?.name || item?.productName || "").trim();
      const qty = Math.max(1, Number(item?.quantity || item?.qty || 1) || 1);
      return name ? `${name} (${qty})` : "";
    })
    .filter(Boolean)
    .join(", ") || String(fallback || "-");
};

const getTotalQty = (items, fallback = 1) => {
  if (!Array.isArray(items) || items.length === 0) {
    return Math.max(1, Number(fallback || 1) || 1);
  }
  return Math.max(
    1,
    items.reduce((sum, item) => sum + (Number(item?.quantity || item?.qty || 0) || 0), 0)
  );
};

const mapSalesRecordToWarehouseRow = (sale, scannedToken, scanType = "RTO", entrySource = "scan") => {
  const awbNumber = String(sale?.awb_no || sale?.awb_number || scannedToken || "").trim();
  const saleOrderId = String(sale?.platform_order_id || sale?.platformOrderId || "").trim();
  const displayOrderId = saleOrderId || awbNumber || (sale?.id != null ? String(sale.id) : "");
  const saleItems = Array.isArray(sale?.items) ? sale.items : [];
  const productName = Array.isArray(sale?.items)
    ? formatItemsSummary(saleItems)
    : String(sale?.items || "-");
  const qty = getTotalQty(saleItems, sale?.total_qty || sale?.qty || 1);

  return {
    id: `${displayOrderId || "order"}-${normalizeScanKey(awbNumber || Date.now())}`,
    orderId: displayOrderId || "-",
    orderSystemId: displayOrderId || awbNumber || "",
    awbNumber: awbNumber || "-",
    productName,
    platform: String(sale?.platform || "-"),
    accountName: String(sale?.account_name || sale?.customer_name || "-"),
    value: Number(sale?.amount ?? 0) || 0,
    qty,
    dispatchDate: formatDispatchDate(sale?.dispatch_date || sale?.dispatchDate),
    type: scanType,
    verification: "Pending",
    entrySource,
  };
};

const mapSaleLookupToWarehouseRow = (payload, scannedToken, scanType = "RTO", entrySource = "scan") => {
  const sale = payload?.sale || {};
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const awbNumber = String(sale?.awb_no || payload?.awb?.awb_number || scannedToken || "").trim();

  return mapSalesRecordToWarehouseRow(
    {
      ...sale,
      awb_no: awbNumber,
      items,
      total_qty: getTotalQty(items, sale?.total_qty || 1),
    },
    awbNumber,
    scanType,
    entrySource
  );
};

export default function RtoWarehouseScanModal({ open, onClose, onSaved, scanType = "RTO" }) {
  const [scanInputValue, setScanInputValue] = useState("");
  const [scannedRows, setScannedRows] = useState([]);
  const [scanProcessing, setScanProcessing] = useState(false);
  const [saveProcessing, setSaveProcessing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [scanFeedback, setScanFeedback] = useState({ type: "", text: "" });
  const [boxStatus, setBoxStatus] = useState("With Box");

  const scanInputRef = useRef(null);
  const scanQueueRef = useRef([]);
  const queueProcessingRef = useRef(false);
  const reservedKeysRef = useRef(new Set());
  const salesLookupRef = useRef({
    loaded: false,
    loadingPromise: null,
    byAwb: new Map(),
  });

  const focusInput = () => {
    setTimeout(() => {
      scanInputRef.current?.focus();
    }, 0);
  };

  const resetModalState = () => {
    setScanInputValue("");
    setScannedRows([]);
    setScanProcessing(false);
    setSaveProcessing(false);
    setPendingCount(0);
    setScanFeedback({ type: "", text: "" });
    setBoxStatus("With Box");
    scanQueueRef.current = [];
    queueProcessingRef.current = false;
    reservedKeysRef.current = new Set();
  };

  useEffect(() => {
    if (!open) return undefined;
    resetModalState();
    const timer = setTimeout(() => {
      focusInput();
    }, 60);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!scanFeedback.text) return undefined;
    const timer = setTimeout(() => {
      setScanFeedback((prev) => (prev.type === "error" ? prev : { type: "", text: "" }));
    }, 1800);
    return () => clearTimeout(timer);
  }, [scanFeedback]);

  const ensureSalesLookup = async () => {
    const cache = salesLookupRef.current;
    if (cache.loaded) return cache;
    if (cache.loadingPromise) return cache.loadingPromise;

    const loadingPromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/sales`);
      if (!response.ok) {
        throw new Error("Unable to load sales lookup.");
      }

      const sales = await response.json();
      const byAwb = new Map();

      if (Array.isArray(sales)) {
        sales.forEach((sale) => {
          const awbKey = normalizeScanKey(sale?.awb_no || sale?.awb_number || sale?.awbNumber);
          if (awbKey && !byAwb.has(awbKey)) byAwb.set(awbKey, sale);
        });
      }

      salesLookupRef.current = {
        loaded: true,
        loadingPromise: null,
        byAwb,
      };

      return salesLookupRef.current;
    })();

    salesLookupRef.current = { ...cache, loadingPromise };

    try {
      return await loadingPromise;
    } catch (error) {
      salesLookupRef.current = {
        loaded: false,
        loadingPromise: null,
        byAwb: new Map(),
      };
      throw error;
    }
  };

  const getRowKey = (row) => {
    const awbKey = normalizeScanKey(row?.awbNumber);
    return awbKey || "";
  };

  const processSingleScan = async ({ rawToken, queueKey, selectedBoxStatus }) => {
    const scanToken = extractScanToken(rawToken);
    if (!scanToken) {
      reservedKeysRef.current.delete(queueKey);
      setScanFeedback({ type: "error", text: "Invalid barcode or AWB." });
      return;
    }

    try {
      let nextRow = null;
      let saleRecord = null;

      try {
        const lookupResponse = await fetch(
          `${API_BASE_URL}/sales/awb/${encodeURIComponent(scanToken)}`
        );
        if (lookupResponse.ok) {
          const lookupPayload = await lookupResponse.json();
          nextRow = mapSaleLookupToWarehouseRow(lookupPayload, scanToken, scanType, "scan");
        }
      } catch (error) {
        console.error("Direct sales AWB lookup error:", error);
      }

      try {
        if (!nextRow) {
          const salesLookup = await ensureSalesLookup();
          saleRecord = salesLookup.byAwb.get(normalizeScanKey(scanToken)) || null;
        }
      } catch (error) {
        console.error("Sales lookup error:", error);
      }

      if (!nextRow && saleRecord) {
        const awbNumber = String(saleRecord?.awb_no || saleRecord?.awb_number || scanToken).trim();
        nextRow = mapSalesRecordToWarehouseRow(saleRecord, awbNumber, scanType, "scan");
      }

      if (!nextRow) {
        throw new Error("Sales record not found for scanned barcode/AWB.");
      }

      nextRow.boxStatus = selectedBoxStatus || "With Box";

      const rowKey = getRowKey(nextRow);
      if (rowKey && reservedKeysRef.current.has(rowKey) && rowKey !== queueKey) {
        reservedKeysRef.current.delete(queueKey);
        setScanFeedback({ type: "error", text: "Order already scanned" });
        return;
      }

      setScannedRows((prev) => [nextRow, ...prev]);
      if (rowKey) reservedKeysRef.current.add(rowKey);
      setScanFeedback({ type: "success", text: "Order scanned" });
    } catch (error) {
      console.error("Warehouse scan error:", error);
      reservedKeysRef.current.delete(queueKey);
      setScanFeedback({
        type: "error",
        text: error?.message || "Unable to process scan. Please try again.",
      });
    }
  };

  const processQueue = async () => {
    if (queueProcessingRef.current) return;
    queueProcessingRef.current = true;
    setScanProcessing(true);

    try {
      while (scanQueueRef.current.length > 0) {
        const nextScan = scanQueueRef.current.shift();
        setPendingCount(scanQueueRef.current.length);
        // eslint-disable-next-line no-await-in-loop
        await processSingleScan(nextScan);
      }
    } finally {
      queueProcessingRef.current = false;
      setScanProcessing(false);
      setPendingCount(0);
      focusInput();
    }
  };

  const handleScanSubmit = (event) => {
    event.preventDefault();
    const parsedToken = extractScanToken(scanInputValue);
    const queueKey = normalizeScanKey(parsedToken || scanInputValue);
    setScanInputValue("");
    focusInput();

    if (!queueKey) {
      setScanFeedback({ type: "error", text: "Please scan a valid barcode or AWB." });
      return;
    }

    if (reservedKeysRef.current.has(queueKey)) {
      setScanFeedback({ type: "error", text: "Order already scanned" });
      return;
    }

    reservedKeysRef.current.add(queueKey);
    scanQueueRef.current.push({
      rawToken: parsedToken || scanInputValue.trim(),
      queueKey,
      selectedBoxStatus: boxStatus,
    });
    setPendingCount(scanQueueRef.current.length);
    setScanFeedback({ type: "", text: "" });
    processQueue();
  };

  const handleClose = () => {
    if (saveProcessing) return;
    resetModalState();
    if (typeof onClose === "function") onClose();
  };

  const handleRemoveRow = (row) => {
    if (!row) return;
    const rowKey = getRowKey(row);
    setScannedRows((prev) => prev.filter((item) => item.id !== row.id));
    if (rowKey) {
      reservedKeysRef.current.delete(rowKey);
    }
    setScanFeedback({ type: "info", text: "Scanned row removed." });
    focusInput();
  };

  const handleSave = async () => {
    if (saveProcessing || scanProcessing || scannedRows.length === 0) return;
    setSaveProcessing(true);
    setScanFeedback({ type: "", text: "" });

    try {
      const rowsForSave = [];

      for (const row of scannedRows) {
        rowsForSave.push({
          ...row,
          type: scanType,
          verification: row.verification || "Pending",
          entrySource: row.entrySource || "scan",
        });
      }

      if (rowsForSave.length === 0) {
        setScanFeedback({
          type: "error",
          text: "No scanned order could be saved.",
        });
        setSaveProcessing(false);
        return;
      }

      if (typeof onSaved === "function") {
        await onSaved(rowsForSave);
      }

      resetModalState();
      if (typeof onClose === "function") onClose();
    } catch (error) {
      console.error("Batch save error:", error);
      setScanFeedback({ type: "error", text: "Failed to save scanned orders." });
    } finally {
      setSaveProcessing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-card scan-modal warehouse-scan-modal">
        <div className="modal-header">
          <div className="warehouse-scan-title-block">
            <h3>RTO Warehouse Scan</h3>
            <p>Scan AWB and review entries before saving.</p>
          </div>
          <button className="btn-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        <div className="warehouse-scan-body">
          <div className="warehouse-scan-stats">
            <div className="warehouse-scan-stat-card">
              <span className="warehouse-scan-stat-label">Total Scan</span>
              <strong>{scannedRows.length}</strong>
            </div>
            <div className="warehouse-scan-stat-card success">
              <span className="warehouse-scan-stat-label">Ready To Save</span>
              <strong>{scannedRows.length}</strong>
            </div>
            <div className="warehouse-scan-stat-card info">
              <span className="warehouse-scan-stat-label">Pending Queue</span>
              <strong>{pendingCount}</strong>
            </div>
          </div>

          <div className="warehouse-scan-input-section">
            <form onSubmit={handleScanSubmit} className="warehouse-scan-input-form">
              <input
                ref={scanInputRef}
                className="warehouse-scan-input"
                type="text"
                value={scanInputValue}
                onChange={(event) => setScanInputValue(event.target.value)}
                placeholder="Scan barcode or AWB number"
                autoFocus
              />
            </form>

            <label className="warehouse-scan-box-status">
              <span>Package</span>
              <select value={boxStatus} onChange={(event) => setBoxStatus(event.target.value)}>
                <option value="With Box">With Box</option>
                <option value="Without Box">Without Box</option>
              </select>
            </label>

            {(scanProcessing || pendingCount > 0) && (
              <div className="warehouse-scan-status">
                Processing scans{pendingCount > 0 ? ` (${pendingCount} pending)` : "..."}
              </div>
            )}

            {!!scanFeedback.text && (
              <div className={`warehouse-scan-feedback ${scanFeedback.type || "info"}`}>
                {scanFeedback.text}
              </div>
            )}
          </div>

          <div className="warehouse-scan-note">
            Duplicate scans stay blocked automatically. Keep scanning continuously.
          </div>

          <div className="warehouse-scan-table-wrap">
            <table className="warehouse-scan-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>AWB Number</th>
                  <th>Qty</th>
                  <th>Platform</th>
                  <th>Account Name</th>
                  <th>Dispatch Date</th>
                  <th>Type</th>
                  <th>Package</th>
                  <th>Added Through</th>
                  <th>Verification</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scannedRows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="warehouse-scan-empty">
                      No products scanned yet
                    </td>
                  </tr>
                ) : (
                  scannedRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.productName}</td>
                      <td>{row.awbNumber}</td>
                      <td>{Number(row.qty || 1)}</td>
                      <td>{row.platform || "-"}</td>
                      <td>{row.accountName || "-"}</td>
                      <td>{row.dispatchDate || "-"}</td>
                      <td>{row.type}</td>
                      <td>{row.boxStatus || "With Box"}</td>
                      <td>{String(row.entrySource || "scan").toUpperCase()}</td>
                      <td>
                        <span className={`warehouse-scan-badge ${String(row.verification || "Pending").toLowerCase()}`}>
                          {row.verification || "Pending"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="warehouse-scan-action-btn"
                          onClick={() => handleRemoveRow(row)}
                          aria-label={`Remove ${row.productName}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="warehouse-scan-footer">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saveProcessing || scanProcessing || scannedRows.length === 0}
          >
            {saveProcessing ? "Saving..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
